"use client";

import { useFeatureFlagEnabled } from "posthog-js/react";
import { createContext, useContext, useMemo } from "react";

// Define the type for feature flag values
export type PostHogFlags = Record<string, string | boolean>;

// Create context for PostHog feature flags
const PostHogFlagsContext = createContext<PostHogFlags>({});

// Hook to access PostHog feature flags
export const usePostHogBootstrappedFlags = () => {
  const context = useContext(PostHogFlagsContext);

  if (context === undefined) {
    throw new Error(
      "usePostHogFlags must be used within a PostHogFlagsProvider",
    );
  }

  return context;
};

/**
 * Hook to access a specific feature flag. DO NOT USE DIRECTLY IN PAGE ELEMENT, YOU MUST USE IT IN A CHILD.
 * @param flagKey - The key of the feature flag to access
 * @returns The value of the feature flag
 */
export const usePostHogFlag = (flagKey: string) => {
  const bootstrappedFlags = usePostHogBootstrappedFlags();

  const bootstrappedFlag = bootstrappedFlags[flagKey];
  const flagFromPostHog = useFeatureFlagEnabled(flagKey);

  // if we haven't received the posthog flag yet, use the flag from the context. the context
  // values are boostrapped from the server.
  return flagFromPostHog ?? bootstrappedFlag;
};

// Provider component that makes the flags available throughout the app
export function PostHogFlagsProvider({
  flags,
  children,
}: {
  flags: PostHogFlags;
  children: React.ReactNode;
}) {
  // Memoize the flags to prevent unnecessary re-renders
  const memoizedFlags = useMemo(() => flags, [flags]);

  return (
    <PostHogFlagsContext.Provider value={memoizedFlags}>
      {children}
    </PostHogFlagsContext.Provider>
  );
}
